import React, { useMemo } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Icon,
  StepConnector,
  stepConnectorClasses,
  type StepIconProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";

type RpStepsProps = {
  activeStep?: number;
  steps?: React.ReactNode[];
  type?: string;
  connector?: React.ReactElement;
};

export default function RpSteps({
  activeStep = 0,
  steps = [],
  type = "",
  connector,
}: RpStepsProps) {
  return (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      connector={connector || <RpStepConnector />}
    >
      {steps.map((label, index) => (
        <Step key={index}>
          <StepLabel
            StepIconComponent={(props) => <StepIcon {...props} type={type} />}
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

/**
 * Custom Step Icon
 */
const StyledIcon = styled("div", {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "completed",
})<{ active?: boolean; completed?: boolean }>(({ active, completed }) => ({
  backgroundColor: "var(--neutral-200)",
  zIndex: 1,
  border: "1px solid var(--neutral-200)",
  color: "var(--neutral-white)",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "var(--radius-round)",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "1.25rem",
  margin: "0 auto",
  transition: "all 0.3s ease",

  ...(active && {
    background: "var(--primary-base)",
    boxShadow: "none",
  }),
  ...(completed && {
    background: "var(--primary-base)",
    boxShadow: "none",
  }),
}));

function StepIcon(props: StepIconProps & { type?: string }) {
  const { icon, active, completed, type } = props;

  const icons = useMemo(() => {
    if (type === "ms_activation") {
      return {
        1: <Icon>assignment</Icon>,
        2: <Icon>auto_fix_high</Icon>,
        3: <Icon>request_quote</Icon>,
      };
    }
    return {
      1: <Icon>fact_check</Icon>,
      2: <Icon>receipt</Icon>,
      3: <Icon>payment</Icon>,
    };
  }, [type]);

  return (
    <StyledIcon
      active={active}
      completed={completed}
      className={clsx({
        "MuiStepIcon-active": active,
        "MuiStepIcon-completed": completed,
      })}
    >
      {icons[String(icon)]}
    </StyledIcon>
  );
}

/**
 * Custom Connector
 */
const RpStepConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: "22px",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundColor: "var(--primary-base)",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: "var(--primary-base)",
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "var(--neutral-200)",
    borderRadius: "var(--radius-round)",
  },
}));
